import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatEUR } from "@/lib/constants";
import { Sun, PhoneCall, CheckCircle, MessageCircle, ShieldCheck, Sparkles, Info, Wallet, Award } from "lucide-react";
import { toast } from "sonner";
import { SOLUTION_LABEL, SOLUTION_ICON } from "@/components/EnergyBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = "https://images.unsplash.com/photo-1776617130051-5a66d8e6d163?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2xhciUyMHBhbmVscyUyMHJvb2YlMjBob3VzZSUyMGNsZWFyJTIwc2t5fGVufDB8fHx8MTc4MDQ4NDIwM3ww&ixlib=rb-4.1.0&q=85";

export default function PublicOffer() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/public/offers/${id}`); setData(r.data); }
      catch { setData({ error: true }); }
    })();
  }, [id]);

  const act = async (action, text = "") => {
    try {
      setSending(true);
      await axios.post(`${API}/public/offers/${id}/action`, { action, text });
      setDone(action);
      setAskOpen(false);
      setMsg("");
      toast.success("Vielen Dank – wir melden uns kurzfristig.");
    } catch { toast.error("Fehler – bitte später erneut versuchen."); }
    finally { setSending(false); }
  };

  const submitQuestion = () => {
    const t = msg.trim();
    if (!t) { toast.error("Bitte formulieren Sie Ihre Frage."); return; }
    act("ask_question", t);
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Wird geladen …</div>;
  if (data.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Angebot nicht gefunden.</div>;
  const { offer, customer_name, company } = data;
  const primary = company?.primary_color || "#f97316";
  const accent = company?.accent_color || "#0f172a";

  // Energie-Erweiterung Phase E — Preisschätzungs-Darstellung
  if (offer?.offer_type === "estimate") {
    return (
      <EstimateView
        offer={offer}
        customer_name={customer_name}
        company={company}
        primary={primary}
        accent={accent}
        done={done}
        sending={sending}
        askOpen={askOpen}
        setAskOpen={setAskOpen}
        msg={msg}
        setMsg={setMsg}
        act={act}
        submitQuestion={submitQuestion}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-offer-page" style={{ "--brand-primary": primary, "--brand-accent": accent }}>
      <div className="relative h-64 md:h-80">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}E6 0%, ${accent}B3 60%, ${primary}66 100%)` }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 h-full flex flex-col justify-end pb-8 text-white">
          <div className="flex items-center gap-3 mb-3" data-testid="public-offer-brand">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company?.name || "Logo"} data-testid="public-offer-logo" className="h-10 max-w-[140px] object-contain bg-white/95 rounded p-1.5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primary }}><Sun className="w-4 h-4 text-white" /></div>
            )}
            <span className="font-semibold">{company?.name || "PV-Angebot"}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Ihr persönliches PV-Angebot
          </h1>
          <p className="mt-2 text-slate-200">für {customer_name}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <Card className="border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope, sans-serif" }}>Überblick</h2>
          <p className="text-slate-700 leading-relaxed">{offer.summary}</p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-orange-50 border border-orange-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-orange-700">Geschätzte Einsparung</p>
              <p className="text-xl font-bold text-orange-900 mt-1">{offer.estimated_savings}</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gesamtpreis</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{formatEUR(offer.total_price)}</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Garantie</p>
              <p className="text-xl font-bold text-emerald-900 mt-1">25 Jahre</p>
            </div>
          </div>
        </Card>

        <Card className="border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Leistungsumfang</h2>
          <div className="divide-y divide-slate-100">
            {offer.items.map((it) => (
              <div key={it.title} className="py-3 flex justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-900">{it.title}</p>
                  <p className="text-sm text-slate-500">{it.description}</p>
                </div>
                <p className="font-semibold tabular-nums whitespace-nowrap">{formatEUR(it.total)}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-bold text-slate-900 pt-4 mt-4 border-t border-slate-200 text-lg">
            <span>Gesamt</span><span>{formatEUR(offer.total_price)}</span>
          </div>
        </Card>

        {offer.options?.length > 0 && (
          <Card className="border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Optionale Zusatzleistungen</h2>
            <div className="space-y-3">
              {offer.options.map((o) => (
                <div key={o.title} className="flex justify-between p-3 rounded-lg bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-900">{o.title}</p>
                    <p className="text-sm text-slate-500">{o.description}</p>
                  </div>
                  <p className="font-semibold tabular-nums">{formatEUR(o.unit_price)}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Ihre Vorteile</h2>
          <ul className="space-y-2">
            {offer.benefits.map((b) => (
              <li key={b} className="flex gap-2 items-start text-slate-700"><CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /><span>{b}</span></li>
            ))}
          </ul>
        </Card>

        <Card className="border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>Häufige Fragen</h2>
          <div className="space-y-4">
            {offer.faq.map((f) => (
              <div key={f.q}>
                <p className="font-semibold text-slate-900">{f.q}</p>
                <p className="text-sm text-slate-600 mt-1">{f.a}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-orange-200 bg-orange-50 p-8 text-center" style={{ borderColor: `${primary}33`, background: `${primary}0D` }}>
          <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: primary }} />
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Wie möchten Sie weitermachen?</h2>
          <p className="text-slate-700 mt-2 mb-6">Wählen Sie eine Option – wir melden uns kurzfristig persönlich bei Ihnen.</p>
          {done ? (
            <div className="font-semibold" style={{ color: accent }}>Danke – wir melden uns bei Ihnen!</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl mx-auto">
              <Button data-testid="public-book-call" onClick={() => act("book_call")} className="text-white hover:opacity-90 transition-opacity" style={{ background: primary }}>
                <PhoneCall className="w-4 h-4 mr-2" /> Rückruf buchen
              </Button>
              <Button data-testid="public-confirm" onClick={() => act("confirm_interest")} variant="outline" className="border-slate-300">
                <CheckCircle className="w-4 h-4 mr-2" /> Interesse bestätigen
              </Button>
              <Button data-testid="public-question" onClick={() => setAskOpen(true)} variant="outline" className="border-slate-300">
                <MessageCircle className="w-4 h-4 mr-2" /> Frage stellen
              </Button>
            </div>
          )}
        </Card>

        <Dialog open={askOpen} onOpenChange={(o) => { if (!sending) setAskOpen(o); }}>
          <DialogContent data-testid="ask-question-dialog" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Frage stellen</DialogTitle>
              <DialogDescription>Schreiben Sie uns Ihre Frage – wir melden uns persönlich bei Ihnen.</DialogDescription>
            </DialogHeader>
            <Textarea
              data-testid="ask-question-textarea"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="z. B. Wie lange dauert die Installation?"
              rows={5}
              autoFocus
            />
            <DialogFooter className="gap-2 sm:gap-2">
              <Button data-testid="ask-question-cancel" variant="outline" onClick={() => setAskOpen(false)} disabled={sending}>Abbrechen</Button>
              <Button data-testid="ask-question-submit" onClick={submitQuestion} disabled={sending || !msg.trim()} className="text-white hover:opacity-90 transition-opacity" style={{ background: primary }}>
                {sending ? "Sende …" : "Absenden"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Branded Footer */}
        <div className="border-t border-slate-200 pt-8 pb-4 mt-4" data-testid="public-offer-footer">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <p className="font-semibold text-slate-900 mb-1">{company?.name || ""}</p>
              {company?.address && <p className="text-slate-600">{company.address}</p>}
              {company?.website && (
                <a href={company.website} target="_blank" rel="noreferrer" className="text-slate-600 hover:underline" style={{ color: primary }}>
                  {company.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Kontakt</p>
              {company?.support_phone && <p className="text-slate-600">Tel: {company.support_phone}</p>}
              {company?.support_email && <p className="text-slate-600">E-Mail: <a href={`mailto:${company.support_email}`} className="hover:underline" style={{ color: primary }}>{company.support_email}</a></p>}
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Rechtliches</p>
              <div className="space-y-1">
                {company?.imprint_url && <a data-testid="footer-imprint" href={company.imprint_url} target="_blank" rel="noreferrer" className="block text-slate-600 hover:underline" style={{ color: primary }}>Impressum</a>}
                {company?.privacy_url && <a data-testid="footer-privacy" href={company.privacy_url} target="_blank" rel="noreferrer" className="block text-slate-600 hover:underline" style={{ color: primary }}>Datenschutz</a>}
                {!company?.imprint_url && !company?.privacy_url && <p className="text-xs text-slate-400">—</p>}
              </div>
            </div>
          </div>

          <div className="text-center text-xs text-slate-400 pt-6 pb-2 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Dieses Angebot ist ein Entwurf. Verbindliche Preise und technische Details werden nach finaler Vor-Ort-Prüfung schriftlich bestätigt.
          </div>
          <div className="text-center text-[10px] text-slate-400 pb-12" data-testid="public-offer-revenioo-line">
            Powered by <a href="https://revenioo.ai" target="_blank" rel="noreferrer" className="font-semibold hover:underline">revenioo.ai</a>
            <span className="mx-1.5">·</span>
            <a href="https://revenioo.ai/impressum" target="_blank" rel="noreferrer" className="hover:underline">Impressum</a>
            <span className="mx-1.5">·</span>
            <a href="https://revenioo.ai/datenschutz" target="_blank" rel="noreferrer" className="hover:underline">Datenschutz</a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============= Energie-Erweiterung Phase E — Estimate-Darstellung =============

const CTA_BY_SOLUTION = {
  pv: "Beratung zur Solaranlage anfragen",
  heat_pump: "Förderanalyse und Heizlastberechnung anfragen",
  pv_heat_pump: "Gesamtsystem-Beratung anfragen",
};

function formatRange(range) {
  if (!Array.isArray(range) || range.length !== 2) return null;
  const [lo, hi] = range;
  if (lo == null || hi == null) return null;
  if (lo === hi) return formatEUR(lo);
  return `${formatEUR(lo)} – ${formatEUR(hi)}`;
}

function EstimateView({ offer, customer_name, company, primary, accent, done, sending, askOpen, setAskOpen, msg, setMsg, act, submitQuestion }) {
  const solutionType = offer.solution_type || "pv";
  const SolutionIcon = SOLUTION_ICON[solutionType] || Sun;
  const solutionLabel = SOLUTION_LABEL[solutionType] || "Solaranlage";
  const ctaLabel = CTA_BY_SOLUTION[solutionType] || CTA_BY_SOLUTION.pv;

  const priceText = formatRange(offer.price_range);
  const subsidyText = formatRange(offer.subsidy_range);
  const afterSubsidyText = formatRange(offer.price_after_subsidy_range);
  const breakdown = offer.breakdown || {};
  const hasBreakdown = breakdown && Object.keys(breakdown).length > 0;

  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid="public-estimate-page"
      style={{ "--brand-primary": primary, "--brand-accent": accent }}
    >
      {/* Hero */}
      <div className="relative">
        <div
          className="h-56 md:h-72"
          style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}E6 50%, ${primary}99 100%)` }}
        />
        <div className="max-w-3xl mx-auto px-6 -mt-44 md:-mt-52 pb-12">
          <div className="flex items-center gap-3 mb-4 text-white" data-testid="estimate-brand">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company?.name || "Logo"} className="h-10 max-w-[140px] object-contain bg-white/95 rounded p-1.5" data-testid="estimate-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/95"><Sun className="w-5 h-5" style={{ color: primary }} /></div>
            )}
            <span className="font-semibold">{company?.name || "Energie-Konfigurator"}</span>
          </div>

          <Card className="border-slate-200 p-6 md:p-10" data-testid="estimate-card">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: primary }}>
              <SolutionIcon className="w-4 h-4" /> {solutionLabel}
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="estimate-title">
              Ihre persönliche Preisschätzung
            </h1>
            <p className="mt-1 text-slate-600">für {customer_name || "Sie"}</p>

            {/* Preisdarstellung */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {priceText && (
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid="estimate-price">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Geschätzte Investition</p>
                  <p className="text-xl md:text-2xl font-bold text-slate-900 mt-1 tabular-nums">{priceText}</p>
                </div>
              )}
              {subsidyText && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50" data-testid="estimate-subsidy">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Mögliche Förderung</p>
                  <p className="text-xl md:text-2xl font-bold text-emerald-900 mt-1 tabular-nums">{subsidyText}</p>
                </div>
              )}
              {afterSubsidyText && (
                <div className="p-4 rounded-xl border-2 sm:col-span-2" style={{ borderColor: primary, background: `${primary}0F` }} data-testid="estimate-after-subsidy">
                  <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: primary }}><Sparkles className="w-3.5 h-3.5" /> Geschätzte Investition nach Förderung</p>
                  <p className="text-2xl md:text-3xl font-extrabold mt-1 tabular-nums" style={{ color: accent }}>{afterSubsidyText}</p>
                </div>
              )}
            </div>

            {/* Breakdown (für PV+WP Combo) */}
            {hasBreakdown && (
              <div className="mt-6 p-5 rounded-xl border border-slate-200" data-testid="estimate-breakdown">
                <p className="text-sm font-bold text-slate-900 mb-3">Aufteilung nach Komponente</p>
                <BreakdownRows breakdown={breakdown} />
              </div>
            )}

            {/* Nächster Schritt */}
            {offer.next_step_text && (
              <div className="mt-6 p-4 rounded-xl border border-slate-200 bg-white" data-testid="estimate-next-step">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Nächster Schritt</p>
                <p className="text-sm text-slate-700">{offer.next_step_text}</p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="mt-6 p-4 rounded-xl border border-amber-200 bg-amber-50/70 flex items-start gap-2.5" data-testid="estimate-disclaimer">
              <Info className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                {offer.disclaimer_text || (
                  <>
                    <strong>Unverbindliche Preisschätzung.</strong> Diese Beträge sind kein verbindliches Angebot
                    und keine zugesicherte Förderung. Förderhöhen sind antrags- und einzelfallabhängig. Eine
                    verbindliche Auslegung und – bei Wärmepumpen – eine finale Heizlastberechnung erfolgt durch
                    Ihre:n Fachbetrieb nach Vor-Ort-Prüfung.
                  </>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-7 pt-6 border-t border-slate-100 text-center">
              {done ? (
                <p className="font-semibold" style={{ color: accent }} data-testid="estimate-done">
                  Vielen Dank — wir melden uns kurzfristig persönlich bei Ihnen.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    data-testid="estimate-cta-primary"
                    onClick={() => act("book_call")}
                    className="text-white hover:opacity-90 transition-opacity sm:col-span-3"
                    style={{ background: primary }}
                  >
                    <PhoneCall className="w-4 h-4 mr-2" /> {ctaLabel}
                  </Button>
                  <Button data-testid="estimate-confirm" onClick={() => act("confirm_interest")} variant="outline" className="border-slate-300">
                    <CheckCircle className="w-4 h-4 mr-2" /> Interesse bestätigen
                  </Button>
                  <Button data-testid="estimate-question" onClick={() => setAskOpen(true)} variant="outline" className="border-slate-300 sm:col-span-2">
                    <MessageCircle className="w-4 h-4 mr-2" /> Frage stellen
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Frage-Dialog */}
      <Dialog open={askOpen} onOpenChange={(o) => { if (!sending) setAskOpen(o); }}>
        <DialogContent data-testid="ask-question-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Frage stellen</DialogTitle>
            <DialogDescription>Schreiben Sie uns Ihre Frage — wir melden uns persönlich bei Ihnen.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="ask-question-textarea"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="z. B. Was beeinflusst die Förderhöhe?"
            rows={5}
            autoFocus
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="ask-question-cancel" variant="outline" onClick={() => setAskOpen(false)} disabled={sending}>Abbrechen</Button>
            <Button data-testid="ask-question-submit" onClick={submitQuestion} disabled={sending || !msg.trim()} className="text-white hover:opacity-90 transition-opacity" style={{ background: primary }}>
              {sending ? "Sende …" : "Absenden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer (gleiches Branding wie Legacy-Offer) */}
      <div className="max-w-3xl mx-auto px-6 pb-12">
        <div className="border-t border-slate-200 pt-8" data-testid="estimate-footer">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <p className="font-semibold text-slate-900 mb-1">{company?.name || ""}</p>
              {company?.address && <p className="text-slate-600">{company.address}</p>}
              {company?.website && (
                <a href={company.website} target="_blank" rel="noreferrer" className="text-slate-600 hover:underline" style={{ color: primary }}>
                  {company.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Kontakt</p>
              {company?.support_phone && <p className="text-slate-600">Tel: {company.support_phone}</p>}
              {company?.support_email && <p className="text-slate-600">E-Mail: <a href={`mailto:${company.support_email}`} className="hover:underline" style={{ color: primary }}>{company.support_email}</a></p>}
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Rechtliches</p>
              <div className="space-y-1">
                {company?.imprint_url && <a href={company.imprint_url} target="_blank" rel="noreferrer" className="block text-slate-600 hover:underline" style={{ color: primary }}>Impressum</a>}
                {company?.privacy_url && <a href={company.privacy_url} target="_blank" rel="noreferrer" className="block text-slate-600 hover:underline" style={{ color: primary }}>Datenschutz</a>}
                {!company?.imprint_url && !company?.privacy_url && <p className="text-xs text-slate-400">—</p>}
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-slate-400 pt-6 flex items-center justify-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Unverbindliche Preisschätzung. Verbindliche Angaben nach Vor-Ort-Prüfung.
          </div>
          <div className="text-center text-[10px] text-slate-400 pt-3" data-testid="estimate-revenioo-line">
            Powered by <a href="https://revenioo.ai" target="_blank" rel="noreferrer" className="font-semibold hover:underline">revenioo.ai</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function BreakdownRows({ breakdown }) {
  const order = ["pv", "heat_pump", "total", "subsidy", "after_subsidy"];
  const labels = {
    pv: "PV-Anteil",
    heat_pump: "Wärmepumpen-Anteil",
    total: "Gesamt",
    subsidy: "Förderung",
    after_subsidy: "Nach Förderung",
  };
  const isHeading = (k) => k === "total" || k === "after_subsidy";

  const visible = order.filter((k) => breakdown[k] !== undefined && breakdown[k] !== null);
  if (visible.length === 0) return null;
  return (
    <div className="divide-y divide-slate-100">
      {visible.map((k) => {
        const v = breakdown[k];
        const text = Array.isArray(v) ? formatRange(v) : (typeof v === "number" ? formatEUR(v) : String(v));
        return (
          <div
            key={k}
            data-testid={`breakdown-row-${k}`}
            className={`py-2.5 flex items-center justify-between gap-3 ${isHeading(k) ? "font-bold text-slate-900" : "text-slate-700"}`}
          >
            <span className="text-sm">{labels[k] || k}</span>
            <span className="text-sm tabular-nums whitespace-nowrap">{text || "—"}</span>
          </div>
        );
      })}
    </div>
  );
}
